import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import ListOfBearers from './ListOfBearers';
import BearerDetails from './BearerDetails';
import EmergencyContactsModal from './EmergencyContactsModal';

import {
	get_users,
	get_user_details,
	update_user,
	delete_user,
	create_bearer
} from "../tools-network";

import {
	validate_name
} from "../tools";

import { showTemporaryMessage } from "../components/Message";

/*
 * Yes, this is hacky:
 * Whenever reportSelectedBearerId is set, we know that the component is used inside RegisterQrCode
 * (or whatever future component with similar needs) that only wants the user to pick a bearer.
 * That is, there is no need to show bearer details.
 */
const PickBearer = ({
		loginToken,
		loggedInUserType,
		selectedUserId,
		updateUsersState,
		reportSelectedBearerId }) => {

	const { t } = useTranslation();
	const [selectedUserData, setSelectedUserData] = useState(null);

	const isAdminUser = (loggedInUserType == "ADMIN" || loggedInUserType == "SUPERUSER");

	const [newBearerName, setNewBearerName]					= useState('');
	const [createBearerEnabled, setCreateBearerEnabled]		= useState(false);
	const [selectedBearerId, setSelectedBearerId]			= useState(0);
	const [isEditingBearer, setIsEditingBearer]				= useState(false);
	const [isLoadingBearers, setIsLoadingBearers]			= useState(true);
	const [showEmergencyContactsModal, setShowEmergencyContactsModal] = useState(false);

	useEffect(() => {
		reportSelectedBearerId?.(selectedBearerId);
	}, [selectedBearerId]);

	// Dummy value to trigger a refresh of the list of users
	// The list of bearers must be refreshed whenever a bearer is created (which is handled in here),
	// deleted or has its name updated (which is handled in the BearerDetails component)
	const [refreshBearerListTrigger, setRefreshBearerListTrigger] = useState(0);

	function resetNewBearerName() {
		setNewBearerName('');
	}

	const enableUpdateUser = () => {
		console.log("Enable update user.");
	};

	const refreshListOfBearers = () => {
		setRefreshBearerListTrigger(prev => prev + 1);
	};

	async function createBearer() {
		if(!validate_name(newBearerName)) {
			showTemporaryMessage(t("name_is_not_valid"), t("name_contains_invalid_characters"), 'danger');
			return;
		}
		setCreateBearerEnabled(false);

		// Non-admin users always create bearers with their own user_id
		// Actually they are not even allowed to provide the user_id field
		// when creating a new bearer
		let user_id = null;
		if(isAdminUser) {
			user_id = selectedUserId;
		}

		try {
			const res = await create_bearer(loginToken, newBearerName, user_id);
			if(res.success) {
				showTemporaryMessage(t("bearer_successfullt_created"), " ", 'success');
				resetNewBearerName();
				enableUpdateUser();
				// Automatically select the newly created bearer
				if(res.id) {
					setSelectedBearerId(res.id);
				}
				refreshListOfBearers();
				// Show emergency contacts modal after successful bearer creation
				setShowEmergencyContactsModal(true);
			} else {
				showTemporaryMessage("Creating bearer FAILED!", res.msg, 'danger');
				resetNewBearerName();
				enableUpdateUser();
			}
		} catch (err) {
			showTemporaryMessage("Creating bearer FAILED!", err, 'danger');
			resetNewBearerName();
			enableUpdateUser();
		}
	}

	return (
		<>
			<div className="w-full mx-auto py-4">
				<div className="flex flex-row gap-4">
					{/* User details */}
					<div className="flex-wrap content-box gap-2 p-4 w-full md:max-w-lg min-w-0"> {/* Removed bg-white + neumorp temporary */}

						{/* TODO: Disable the ability to create a bearer for now;
							maybe this whole components needs to go if the only place
							a user is allowed to create a bearer is when registering a QR code */}

						{!createBearerEnabled && isAdminUser &&
						<div hidden={createBearerEnabled} className="flex w-full items-center mt-4">
							<button
								onClick={() => 
									{setCreateBearerEnabled(true);}}
								className="create-bearer-enable btn-secondary btn-secondary-hover w-full font-semibold py-2 mx-4 rounded-md text-center text-sm disabled:bg-gray-400 disabled:cursor-not-allowed transition ease-in-out hover:-translate-y-0.5 hover:scale-105 hover:bg-cyan-600 hover:text-white hover:outline-none">
								{t("create_bearer")}
							</button>
						</div>
						}
						
						<div hidden={!createBearerEnabled}>
							<div className="flex flex-col w-full items-center border-b py-4 border-cyan-500">
								<div className="flex w-full items-center">
									<label htmlFor="name" className="bearer-detail bearer-detail-create-bearer text-sm font-semibold leading-4 text-gray-700 mr-1">
										{t("name")}:
									</label>
									<input 
										type="text"
										id="bearer-detail-name"
										value={newBearerName}
										placeholder="Enter name of bearer"
										name="name"
										onChange={(e) => {
													setNewBearerName(e.target.value);}}
										className="bearer-detail block w-full rounded-md border border-gray-300 px-2 py-1 text-gray-900 shadow-sm focus-within:ring-1 focus-within:ring-inset focus-within:ring-cyan-600 sm:max-w-md sm:text-sm leading-6"
									/>
								</div>

								<div className="flex w-full items-center justify-between mt-3 gap-4">
									<button
										onClick={() =>
											{ createBearer(); }}
										className="btn-main w-full text-white px-2 py-1 rounded-md text-center font-bold text-sm disabled:bg-gray-400 disabled:text-grey-600 disabled:cursor-not-allowed transition ease-in-out hover:-translate-y-0.5 hover:scale-105">
										{t("submit_new_bearer")}
									</button>

									<button
										onClick={() =>
											{ resetNewBearerName();
											setCreateBearerEnabled(false); }}
										className="w-auto px-3 py-1 rounded-md text-center font-semibold text-sm text-danger-700 disabled:bg-gray-400 disabled:cursor-not-allowed hover:text-cyan-600">
										{t("cancel")}
									</button>
								</div>
							</div>
						</div>
						<div>
							<ListOfBearers
								loginToken={loginToken}
								userId={selectedUserId}
								setSelectedBearerId={setSelectedBearerId}
							refreshBearerListTrigger={refreshBearerListTrigger}
							isEditingBearer={isEditingBearer} />
							{/* If reportSelectedBearerId is set, we only select a
							  *	bearer and don't
							  * show any more details about bearers*/}
							{ !reportSelectedBearerId && <BearerDetails
								loginToken={loginToken}
								bearerId={selectedBearerId}
								refreshListOfBearers={refreshListOfBearers}
								reportEditingState={setIsEditingBearer} />
								}
						</div>
					</div>
				</div>
			</div>

			{/* Emergency Contacts Modal */}
			{showEmergencyContactsModal && (
				<EmergencyContactsModal
					loginToken={loginToken}
					bearerId={selectedBearerId}
					onClose={() => setShowEmergencyContactsModal(false)}
					onContactAdded={() => {
						// Optional: Could refresh something or show additional message
					}}
				/>
			)}
		</>
	)
};

export default PickBearer;
